import { type BranchKey, useRebaseBranch } from '@festina-life/api'
import { assertNotEmpty } from '@festina-life/core'
import { useState } from 'react'

import { useBaseBranchWorkspaces } from '../../../../hooks/useBaseBranchWorkspaces'
import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import { useInvalidateBranchAfterHistoryRewrite } from '../../../../uiModels/branches/useInvalidateBranchAfterHistoryRewrite'
import { useRebaseWorkspace } from '../../../workspacesPanel/workspaceDetails/manageWorkspace/useRebaseWorkspace'

/**
 * Rebases the branch on its base branch, also rebasing its workspace when it has exactly one. Errors are rethrown so
 * callers can decide what to do next and how to report the outcome.
 */
export const useManageRebaseBranch = (branchKey: BranchKey) => {
  const [isInvalidating, setIsInvalidating] = useState(false)
  const { mutateAsync: rebaseBranch, isPending } = useRebaseBranch()
  const invalidateBranch = useInvalidateBranchAfterHistoryRewrite()
  const specification = useConciseBranch({
    branchName: branchKey.name,
    server: branchKey.server,
    shouldFallbackToStandard: false,
  })
  const rebaseWorkspace = useRebaseWorkspace()
  const branchWorkspaces = useBaseBranchWorkspaces(branchKey)
  const singleWorkspace =
    branchWorkspaces?.length === 1 ? branchWorkspaces[0] : undefined

  assertNotEmpty(specification)

  const handleRebaseBranch = async (isCreatingConflictsWorkspace: boolean) => {
    assertNotEmpty(specification.name)

    try {
      await rebaseBranch({
        branchName: specification.name,
        server: branchKey.server,
      })

      setIsInvalidating(true)
      await invalidateBranch(branchKey)

      if (singleWorkspace && !isCreatingConflictsWorkspace) {
        const workspaceKey = {
          name: singleWorkspace.name ?? '',
          branchKey,
        }
        await rebaseWorkspace(workspaceKey)
      }
    } finally {
      setIsInvalidating(false)
    }
  }

  return {
    rebase: handleRebaseBranch,
    isRebasing: isPending || isInvalidating,
  }
}
